import { Component, OnDestroy, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subject, catchError, concatMap, finalize, from, takeUntil, tap } from 'rxjs';

import { IndicacaoService } from '../../../../../services/indicacoes/indicacao.service';
import {
  CancelamentoLoteResultadoDTO,
  ComissaoAdminConsultaDTO,
  ComissaoAdminFiltroDTO,
  ComissaoAdminResumoDTO,
  ComissaoRelatorioPdfFiltroDTO,
  PagamentoGeradoLoteDTO,
  RepasseComissaoPorValorConsultaDTO,
  RepasseLoteIgnoradaDTO,
  RepasseLoteResultadoDTO,
  StatusRepasse,
} from '../../../../../models/indicacoes/indicacao.model';
import { PeriodoFiltro } from '../../../../../models/dashboard/periodo-filtro.model';
import { FileUtils } from '../../../../../shared/utils/file-utils';

/** Severities aceitas pelo p-tag do PrimeNG. */
type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast';

/**
 * Linha editavel do dialog de lote itemizado. Guarda a comissao de origem (para
 * saldo/identificacao), o `valor` a pagar (pre-preenchido com o saldo) e a mensagem
 * de erro inline da validacao client-side (0 < valor <= saldo).
 */
interface LoteItemEdicao {
  comissao: ComissaoAdminConsultaDTO;
  valor: number | null;
  erro: string;
}

/** Campos de ordenacao permitidos pelo contrato (whitelist). */
const CAMPOS_ORDENACAO: string[] = ['competencia', 'valorComissao', 'statusRepasse', 'geradoEm'];

/**
 * Subcomponente da aba "Repasse de Comissoes" (master-only).
 *
 * Lista global de comissoes de todos os indicadores (GET /comissoes), paginada
 * server-side, com card de totais (resumo dos filtros aplicados), filtros
 * dinamicos e acao de "marcar como pago" (PATCH /comissoes/{id}/repasse).
 * Acesso restrito via `masterAccessGuard` na rota pai.
 * Fonte: `vendas/docs/api/API_CONTRACT_repasse_comissoes.md`.
 */
@Component({
  selector: 'app-repasse-comissoes',
  templateUrl: './repasse-comissoes.component.html',
  styleUrls: ['./repasse-comissoes.component.scss'],
  providers: [MessageService],
})
export class RepasseComissoesComponent implements OnInit, OnDestroy {
  /** Encerra as assinaturas (onLangChange) no ngOnDestroy. */
  private destroy$ = new Subject<void>();

  // ===========================================================================
  // Listagem (tabela lazy)
  // ===========================================================================

  comissoes: ComissaoAdminConsultaDTO[] = [];
  first = 0;
  rows = 10;
  totalRecords = 0;
  loading = true;
  erroCarregar = false;

  /** Opcoes do dropdown "itens por pagina" do footer. */
  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  /** Ultimo valor de "itens por pagina" ja carregado; usado para descartar o
   *  (ngModelChange) que o dropdown dispara sem o usuario mudar a selecao. */
  private _lastRows = 10;

  /** Ordenacao atual no formato "campo,direcao" (default geradoEm,desc). */
  sortAtual = 'geradoEm,desc';

  /** Itens do menu popup "Exportar" (PDF / Excel .xlsx / CSV). Reconstruido no onLangChange. */
  exportarItens: MenuItem[] = [];

  /** Loading unico do botao "Exportar" (spinner + disabled; anti-clique-duplo p/ os 3
   *  formatos — PDF, XLSX, CSV — pois so ha um export por vez). */
  exportando = false;

  /** Id da comissao cujo(s) comprovante(s) esta(o) sendo baixado(s) agora (spinner na
   *  linha + guarda anti-clique-duplo). `null` = nenhuma impressao em andamento. */
  imprimindoComissaoId: number | null = null;

  // ===========================================================================
  // Resumo (totais dos filtros aplicados — re-lido a cada carga)
  // ===========================================================================

  resumo: ComissaoAdminResumoDTO = this.resumoZerado();

  // ===========================================================================
  // Filtros
  // ===========================================================================

  /** Status filtrado (null = todos). */
  statusAtual: StatusRepasse | null = null;

  /** Competencia (par datetime "dd/MM/yyyy'T'HH:mm") vindo do app-periodo-filtro. */
  competenciaDataInicio: string | undefined = undefined;
  competenciaDataFim: string | undefined = undefined;

  somenteContadores = false;

  /**
   * Indicador (empresa indicadora que recebe) selecionado no filtro. `null` = todos.
   * Alimenta o filtro `indicadorId` (server-side) e habilita o pagamento por valor.
   */
  indicadorSelecionadoId: number | null = null;

  /** Filtro por cupom (server-side, via id do cupom da indicacao). */
  cupomSelecionadoId: number | null = null;
  /** Opcoes do dropdown de cupom (derivadas das comissoes carregadas). */
  cupomOptions: { label: string; value: number }[] = [];

  /**
   * Opcoes do dropdown de indicador (FormDTO { label, value } = { nome, empresaId }).
   * Nao ha endpoint dedicado de indicadores: derivamos os distintos de uma carga ampla
   * de /comissoes (ver `carregarIndicadores`).
   */
  indicadorOptions: { label: string; value: number }[] = [];

  /** Opcoes do filtro de status (label reconstruido no onLangChange). */
  statusOptions: { label: string; value: StatusRepasse | null }[] = [];

  // ===========================================================================
  // Selecao multipla + aprovacao em lote
  // ===========================================================================

  /** Comissoes marcadas na tabela (apenas PENDENTES sao selecionaveis). */
  selecionadas: ComissaoAdminConsultaDTO[] = [];

  /** Dialog de confirmacao do lote "marcar como pagas". */
  loteDialogVisivel = false;

  /**
   * Itens editaveis do dialog de lote (um por comissao selecionada). O valor de cada
   * um e pre-preenchido com o saldo (quitacao integral por padrao); reduzir o valor
   * registra pagamento parcial. Reconstruido a cada abertura do dialog.
   */
  loteItens: LoteItemEdicao[] = [];

  // ===========================================================================
  // Dialog de confirmacao — marcar como pago
  // ===========================================================================

  pagarDialogVisivel = false;
  comissaoSelecionada: ComissaoAdminConsultaDTO | null = null;
  processando = false;

  /** Valor a repassar no dialog individual. Pre-preenchido com o saldo (pagamento
   *  integral por padrao); o usuario pode reduzir para registrar pagamento parcial. */
  valorPagar = 0;

  /** Mensagem de erro do campo "valor a pagar" (validacao client-side). */
  erroValorPagar = '';

  // ===========================================================================
  // Dialog — pagar POR VALOR (todas as pendentes de um indicador)
  // ===========================================================================

  pagarPorValorDialogVisivel = false;
  /** Valor total a repassar ao indicador selecionado (Guarani, sem casas). */
  valorTotalPagar: number | null = null;
  /** Erro de validacao client-side do valor total. */
  erroValorTotal = '';

  // ===========================================================================
  // Dialog de confirmacao — cancelar comissao
  // ===========================================================================

  cancelarDialogVisivel = false;

  // ===========================================================================
  // Dialog de confirmacao — cancelar comissoes em lote
  // ===========================================================================

  cancelarLoteDialogVisivel = false;

  constructor(
    private indicacaoService: IndicacaoService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.buildStatusOptions();
    this.montarExportarItens();
    // Reconstroi os rotulos do filtro de status E do menu "Exportar" ao trocar o
    // idioma (pt-BR <-> es). Arrow expandido para bloco (OBS-2 do plan-review).
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.buildStatusOptions();
      this.montarExportarItens();
    });

    // Popula o dropdown de indicadores (derivado de /comissoes — sem endpoint
    // dedicado). A carga da TABELA e disparada pelo (onLazyLoad) da p-table.
    this.carregarIndicadores();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Popula o dropdown de indicadores. Como NAO ha endpoint dedicado que liste os
   * indicadores, derivamos os DISTINTOS de uma carga ampla de /comissoes
   * (empresaId + nome), respeitando o filtro "somente contadores". Ordenado por nome.
   */
  private carregarIndicadores(): void {
    this.indicacaoService
      .listarComissoesAdmin({
        somenteContadores: this.somenteContadores ? true : undefined,
        page: 0,
        size: 500,
        sort: 'geradoEm,desc',
      })
      .subscribe({
        next: (res) => {
          const distintos = new Map<number, string>();
          for (const c of res.comissoes?.content || []) {
            if (c.indicadorEmpresaId != null && !distintos.has(c.indicadorEmpresaId)) {
              distintos.set(c.indicadorEmpresaId, c.indicadorNome || `#${c.indicadorEmpresaId}`);
            }
          }
          this.indicadorOptions = Array.from(distintos, ([value, label]) => ({ value, label })).sort(
            (a, b) => a.label.localeCompare(b.label),
          );
          // Deriva tambem os cupons distintos (id + nome) da mesma carga ampla.
          const cupons = new Map<number, string>();
          for (const c of res.comissoes?.content || []) {
            if (c.cupomId != null && !cupons.has(c.cupomId)) {
              cupons.set(c.cupomId, c.cupomNome || `#${c.cupomId}`);
            }
          }
          this.cupomOptions = Array.from(cupons, ([value, label]) => ({ value, label })).sort(
            (a, b) => a.label.localeCompare(b.label),
          );
          if (
            this.cupomSelecionadoId != null &&
            !this.cupomOptions.some((o) => o.value === this.cupomSelecionadoId)
          ) {
            this.cupomSelecionadoId = null;
          }
          // Se o indicador selecionado saiu da lista (ex.: troca de "somente
          // contadores"), limpa a selecao para nao filtrar por um id ausente.
          if (
            this.indicadorSelecionadoId != null &&
            !this.indicadorOptions.some((o) => o.value === this.indicadorSelecionadoId)
          ) {
            this.indicadorSelecionadoId = null;
          }
        },
        error: () => {
          this.indicadorOptions = [];
        },
      });
  }

  /** Nome do indicador selecionado (para o subtitulo do dialog e mensagens). */
  get indicadorSelecionadoNome(): string {
    const opt = this.indicadorOptions.find((o) => o.value === this.indicadorSelecionadoId);
    return opt?.label ?? '';
  }

  /** Reage a troca do dropdown de indicador: recarrega a lista filtrada. */
  onIndicadorChange(): void {
    this.aplicarFiltros();
  }

  /** Reage a troca do dropdown de cupom: recarrega a lista filtrada. */
  onCupomChange(): void {
    this.aplicarFiltros();
  }

  private buildStatusOptions(): void {
    const valores: StatusRepasse[] = ['PENDENTE', 'PAGA', 'CANCELADA'];
    this.statusOptions = [
      { value: null, label: this.translate.instant('contador.repasse.status_todos') },
      ...valores.map((v) => ({
        value: v,
        label: this.translate.instant(`contador.repasse.status.${v}`),
      })),
    ];
  }

  private resumoZerado(): ComissaoAdminResumoDTO {
    return { totalComissoes: 0, totalPendente: 0, totalPago: 0, totalCancelado: 0 };
  }

  // ===========================================================================
  // Listagem
  // ===========================================================================

  private carregar(page: number, size: number): void {
    this.loading = true;
    this.erroCarregar = false;
    // A selecao vale para a visao atual (tabela lazy server-side): limpamos a cada
    // recarga (filtro/pagina/acao) para nao agir sobre linhas que sairam da tela.
    this.selecionadas = [];

    this.indicacaoService.listarComissoesAdmin(this.construirFiltros(page, size)).subscribe({
      next: (res) => {
        this.comissoes = res.comissoes?.content || [];
        this.totalRecords = res.comissoes?.totalElements ?? 0;
        this.resumo = res.resumo || this.resumoZerado();
        this.first = page * size;
        this.loading = false;
      },
      error: (err) => {
        this.comissoes = [];
        this.totalRecords = 0;
        this.resumo = this.resumoZerado();
        this.erroCarregar = true;
        this.loading = false;
        // 422: o backend responde com mensagem amigavel (formato/intervalo de
        // periodo invalido). Nos demais casos, mensagem generica de carga.
        const summary =
          err?.status === 422 && err?.error?.message
            ? err.error.message
            : this.translate.instant('contador.repasse.erro_carregar');
        this.messageService.add({ severity: 'error', summary });
      },
    });
  }

  private construirFiltros(page: number, size: number): ComissaoAdminFiltroDTO {
    return {
      status: this.statusAtual ?? undefined,
      competenciaDataInicio: this.competenciaDataInicio,
      competenciaDataFim: this.competenciaDataFim,
      // Filtro por indicador especifico (server-side) via id da empresa indicadora.
      indicadorId: this.indicadorSelecionadoId ?? undefined,
      // Filtro por cupom especifico (server-side) via id do cupom da indicacao.
      cupomId: this.cupomSelecionadoId ?? undefined,
      somenteContadores: this.somenteContadores ? true : undefined,
      page,
      size,
      sort: this.sortAtual,
    };
  }

  /** Carregamento lazy da tabela (paginacao + ordenacao server-side). */
  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows ?? this.rows;
    const page = Math.floor((event.first ?? 0) / size);
    this.rows = size;
    this._lastRows = size;

    const campo = (event.sortField as string) || 'geradoEm';
    const campoValido = CAMPOS_ORDENACAO.includes(campo) ? campo : 'geradoEm';
    const direcao = event.sortOrder === 1 ? 'asc' : 'desc';
    this.sortAtual = `${campoValido},${direcao}`;

    this.carregar(page, size);
  }

  /** Troca de pagina pelo paginador externo. Como a tabela e lazy,
   *  recarregamos a pagina solicitada do backend. */
  onPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rows;
    const page = event.page ?? 0;
    this.rows = size;
    this._lastRows = size;
    this.carregar(page, size);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega.
   *  O retorno antecipado ignora o disparo do dropdown sem mudanca de valor. */
  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) {
      return;
    }
    this._lastRows = this.rows;
    this.first = 0;
    this.carregar(0, this.rows);
  }

  /**
   * Guarda o par de datas do app-periodo-filtro (strings cruas, sem conversao) e
   * recarrega. Como e um filtro inline no header (sem botao "Filtrar"), aplica ao
   * concluir o modal de periodo — padrao "Extrato de Caixa".
   */
  onPeriodoChanged(p: PeriodoFiltro): void {
    this.competenciaDataInicio = p.dataInicio || undefined;
    this.competenciaDataFim = p.dataFim || undefined;
    this.aplicarFiltros();
  }

  /** Aplica os filtros atuais e recarrega a partir da primeira pagina. */
  aplicarFiltros(): void {
    this.first = 0;
    this.carregar(0, this.rows);
  }

  /** Troca o status filtrado (dropdown do header) e recarrega da primeira pagina. */
  onStatusChange(): void {
    this.aplicarFiltros();
  }

  /** Alterna "somente contadores": recarrega o dropdown de indicadores e a lista. */
  onSomenteContadoresChange(): void {
    this.carregarIndicadores();
    this.aplicarFiltros();
  }

  // ===========================================================================
  // Acao — exportar (menu PDF / Excel / CSV das comissoes com os filtros atuais)
  // ===========================================================================

  /**
   * Monta os 3 itens do menu popup "Exportar" (PDF / Excel .xlsx / CSV) com os
   * rotulos traduzidos. Chamado no ngOnInit e reconstruido a cada troca de idioma
   * (onLangChange). Ordem e icones espelham operar-cliente: PDF usa o fluxo Base64
   * existente (`exportarPdf`); Excel/CSV usam o export binario (`exportarArquivo`).
   */
  private montarExportarItens(): void {
    this.exportarItens = [
      {
        label: this.translate.instant('contador.repasse.exportar.pdf'),
        icon: 'pi pi-file-pdf',
        command: () => this.exportarPdf(),
      },
      {
        label: this.translate.instant('contador.repasse.exportar.excel'),
        icon: 'pi pi-file-excel',
        command: () => this.exportarArquivo('xlsx'),
      },
      {
        label: this.translate.instant('contador.repasse.exportar.csv'),
        icon: 'pi pi-file',
        command: () => this.exportarArquivo('csv'),
      },
    ];
  }

  /**
   * Exporta o PDF do "Reparto de Comisiones" respeitando os filtros ATUAIS da tela
   * (status, periodo, indicador, "somente contadores", ordenacao) — SEM page/size
   * (imprime TODAS as comissoes do filtro ate o teto do backend). Espelha o
   * `operar-cliente.gerarLivroDeVendas`: sucesso => baixa o PDF; base64 vazio =>
   * "sem dados" (defensivo — o backend gera PDF valido mesmo com 0 linhas); erro
   * => toast. `exportando` desabilita o botao e evita clique duplo.
   */
  exportarPdf(): void {
    if (this.exportando) {
      return; // guarda contra clique duplo
    }
    this.exportando = true;
    this.indicacaoService.gerarRelatorioComissoesPdf(this.construirFiltrosRelatorio()).subscribe({
      next: (base64) => {
        this.exportando = false;
        if (base64 && base64.length > 0) {
          FileUtils.abrirPdf(base64, this.nomeArquivoRelatorio('pdf'));
        } else {
          // Defensivo: contrato diz que 0 comissoes ainda gera PDF valido (cards zerados).
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('contador.repasse.exportar_pdf.sem_dados'),
          });
        }
      },
      error: (err) => {
        this.exportando = false;
        this.tratarErroExportarPdf(err);
      },
    });
  }

  /**
   * Exporta as comissoes como arquivo binario (.csv/.xlsx) com os filtros ATUAIS da tela
   * (mesmos do PDF, via `construirFiltrosRelatorio`). Corpo => baixa via `FileUtils.baixarBlob`;
   * 0 bytes => toast "sem dados" sem baixar; erro => `tratarErroExportarArquivo`. A flag
   * `exportando` (compartilhada com o PDF) evita clique duplo.
   */
  exportarArquivo(formato: 'csv' | 'xlsx'): void {
    if (this.exportando) {
      return; // guarda contra clique duplo
    }
    this.exportando = true;
    this.indicacaoService
      .gerarRelatorioComissoesArquivo(formato, this.construirFiltrosRelatorio())
      .subscribe({
        next: (blob) => {
          this.exportando = false;
          if (blob && blob.size > 0) {
            FileUtils.baixarBlob(blob, this.nomeArquivoRelatorio(formato));
          } else {
            // CA06: backend responde 200 com 0 bytes quando nao ha comissoes no filtro.
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('contador.repasse.exportar_pdf.sem_dados'),
            });
          }
        },
        error: (err) => {
          this.exportando = false;
          this.tratarErroExportarArquivo(err);
        },
      });
  }

  /**
   * Trata o erro do export binario. Como `readBlob` entrega `err.error` como Blob (nao
   * parseavel de forma sincrona), roteamos a mensagem i18n por `err.status`: 403 sem
   * permissao, 422 limite excedido, generico caso contrario.
   */
  private tratarErroExportarArquivo(err: any): void {
    const status = err?.status;
    let chave = 'contador.repasse.exportar_pdf.erro'; // generico/500 (reuso)
    if (status === 403) {
      chave = 'contador.repasse.erros.sem_permissao'; // reuso (existe)
    } else if (status === 422) {
      chave = 'contador.repasse.exportar.limite_excedido'; // chave NEUTRA (OBS-1)
    }
    this.messageService.add({ severity: 'error', summary: this.translate.instant(chave) });
  }

  /**
   * Monta os filtros do relatorio a partir do estado atual da tela. NAO reutiliza
   * `construirFiltros(page, size)` (que sempre injeta paginacao): o relatorio imprime
   * TUDO. `null -> undefined` e `false -> undefined` para o buildQueryParams descartar
   * as chaves vazias (ele so filtra `undefined`). `sortAtual` ja e clampado a whitelist
   * `CAMPOS_ORDENACAO` em `onLazyLoad`, entao vai sempre um valor valido do contrato.
   */
  private construirFiltrosRelatorio(): ComissaoRelatorioPdfFiltroDTO {
    return {
      status: this.statusAtual ?? undefined,
      competenciaDataInicio: this.competenciaDataInicio,
      competenciaDataFim: this.competenciaDataFim,
      indicadorId: this.indicadorSelecionadoId ?? undefined,
      somenteContadores: this.somenteContadores ? true : undefined,
      sort: this.sortAtual,
      // incluirDetalhePagamentos: omitido -> backend usa default true
      // page / size: NAO incluidos (nao existem no tipo) -> relatorio imprime tudo
    };
  }

  /** Nome do arquivo baixado por extensao: `Reparto_Comisiones_AAAAMMDD.{ext}`
   *  (.pdf/.xlsx/.csv; a tela e ES; sugestao do contrato). */
  private nomeArquivoRelatorio(ext: 'pdf' | 'xlsx' | 'csv'): string {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    return `Reparto_Comisiones_${ano}${mes}${dia}.${ext}`;
  }

  /**
   * Trata os erros do endpoint de relatorio (mesma filosofia do `carregar()` e do
   * `tratarErroLivro` de operar-cliente): 403 sem permissao (reuso da chave existente);
   * 422 (teto de 5000 OU formato/intervalo de periodo) exibindo a mensagem amigavel do
   * backend, com fallback a chave nova `limite_excedido`; generico/500 com fallback `erro`.
   */
  private tratarErroExportarPdf(err: any): void {
    const status = err?.status;

    if (status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('contador.repasse.erros.sem_permissao'),
      });
      return;
    }

    // A mensagem amigavel do backend chega em `err.error`. Como o relatorio usa
    // `readText` (responseType 'text'), esse corpo vem como STRING CRUA (o JSON
    // NAO e parseado pelo HttpClient) — diferente das chamadas `read` (JSON), onde
    // `err.error.message` funciona direto (ex.: `carregar()`). Extraimos a mensagem
    // de forma defensiva para NAO cair sempre no fallback (senao um 422 de "periodo
    // invalido" exibiria o texto de "teto excedido").
    const mensagemBackend = this.extrairMensagemBackend(err);

    if (status === 422) {
      // 422 unifica teto e periodo; exibe a mensagem amigavel do backend (que
      // distingue os dois casos) com fallback a chave de teto quando ela faltar.
      this.messageService.add({
        severity: 'error',
        summary:
          mensagemBackend ||
          this.translate.instant('contador.repasse.exportar_pdf.limite_excedido'),
      });
      return;
    }

    // 500 e demais: mensagem do backend quando houver, senao a chave generica nova.
    this.messageService.add({
      severity: 'error',
      summary: mensagemBackend || this.translate.instant('contador.repasse.exportar_pdf.erro'),
    });
  }

  /**
   * Extrai a mensagem amigavel do backend de um erro HTTP de forma robusta ao
   * `responseType`. Chamadas `read` (JSON) entregam `err.error` como OBJETO
   * (`{ message }`); chamadas `readText` (usada por este relatorio) entregam
   * `err.error` como STRING CRUA (JSON nao parseado). Cobrimos os dois casos:
   * objeto -> `.message`; string -> `JSON.parse` e `.message`. Corpo ausente ou
   * texto nao-JSON retorna `undefined` (o chamador aplica o fallback i18n; evita
   * jogar um corpo cru/HTML gigante dentro do toast).
   */
  private extrairMensagemBackend(err: any): string | undefined {
    const corpo = err?.error;
    if (!corpo) {
      return undefined;
    }
    if (typeof corpo === 'object') {
      return typeof corpo.message === 'string' ? corpo.message : undefined;
    }
    if (typeof corpo === 'string') {
      try {
        const parsed = JSON.parse(corpo);
        return typeof parsed?.message === 'string' ? parsed.message : undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  // ===========================================================================
  // Acao — pagar POR VALOR (todas as pendentes do indicador selecionado)
  // ===========================================================================

  /** So habilita o pagamento por valor quando ha um indicador selecionado. */
  get podePagarPorValor(): boolean {
    return this.indicadorSelecionadoId != null;
  }

  /**
   * Abre o dialog de pagamento por valor com o campo VAZIO. Este fluxo NAO usa a
   * selecao da grade: o backend quita TODAS as pendentes do indicador em FIFO (da
   * mais antiga para a mais recente), independentemente do que estiver marcado. O
   * usuario digita livremente o valor total a repassar.
   */
  abrirDialogPagarPorValor(): void {
    if (!this.podePagarPorValor) {
      return;
    }
    this.valorTotalPagar = null;
    this.erroValorTotal = '';
    this.pagarPorValorDialogVisivel = true;
  }

  fecharDialogPagarPorValor(): void {
    this.pagarPorValorDialogVisivel = false;
    this.valorTotalPagar = null;
    this.erroValorTotal = '';
  }

  /** Limpa o erro do campo ao editar (feedback imediato). */
  onValorTotalChange(): void {
    this.erroValorTotal = '';
  }

  confirmarPagarPorValor(): void {
    if (this.processando || this.indicadorSelecionadoId == null) {
      return;
    }
    // Validacao client-side: APENAS valor > 0. NAO ha teto (`[max]`) aqui, ao
    // contrario do dialog individual (que tem `comissaoSelecionada.saldo`): o unico
    // total pendente conhecido na tela e `resumo.totalPendente`, que reflete os
    // FILTROS ATUAIS (status, competencia) — o backend paga TODAS as pendentes do
    // indicador, sem periodo. Usa-lo como teto bloquearia pagamentos validos
    // (ex.: filtro de competencia ativo). O teto (limite INCLUSIVO) e validado pelo
    // backend, que responde 422 com mensagem amigavel exibida no campo e no toast.
    const valor = this.valorTotalPagar;
    if (valor == null || valor <= 0) {
      this.erroValorTotal = this.translate.instant('contador.repasse.pagar_valor.valor_invalido');
      return;
    }

    this.processando = true;
    this.indicacaoService.repassarPorValor(this.indicadorSelecionadoId, valor).subscribe({
      next: (res) => {
        // Captura o id do pagamento NO INICIO. `null` no caso "sem pendencias"
        // (troco integral) => nenhum recibo a baixar.
        const pagamentoId = res.pagamentoComissaoId ?? null;
        this.processando = false;
        this.pagarPorValorDialogVisivel = false;
        this.valorTotalPagar = null;
        this.erroValorTotal = '';
        this.notificarResultadoPorValor(res);
        // Auto-download do comprovante (best-effort): o repasse JA teve sucesso.
        if (pagamentoId != null) {
          this.baixarComprovante(pagamentoId);
        }
        // Recarrega pagina atual + resumo (status/saldo/repassadoEm/totais).
        this.carregar(this.paginaAtual(), this.rows);
      },
      error: (err) => {
        this.processando = false;
        // 422 = valor recusado pela regra do backend (maior que o total pendente do
        // indicador, ou <= 0). Alem do toast, ecoamos a mensagem NO CAMPO: o dialog
        // continua aberto e o usuario corrige o valor ali mesmo. Nao ha validacao
        // client-side de teto aqui (ver `onValorTotalChange`): o unico total pendente
        // disponivel na tela (`resumo.totalPendente`) reflete os FILTROS, nao o
        // pendente real do indicador — o backend e a fonte da verdade.
        if (err?.status === 422) {
          this.erroValorTotal =
            this.extrairMensagemBackend(err) ||
            this.translate.instant('contador.repasse.pagar_valor.valor_recusado');
        }
        this.tratarErroAcao(err);
      },
    });
  }

  /**
   * Toast do resultado do pagamento por valor: sucesso simples quando tudo foi
   * aplicado; alerta com o TROCO quando `valorNaoUtilizado > 0` (valor informado
   * excedeu o total pendente do indicador).
   */
  private notificarResultadoPorValor(res: RepasseComissaoPorValorConsultaDTO): void {
    const guarani = (v: number): string =>
      `Gs ${(v ?? 0).toLocaleString('es-PY', { maximumFractionDigits: 0 })}`;

    if ((res.valorNaoUtilizado ?? 0) > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.repasse.pagar_valor.sucesso'),
        detail: this.translate.instant('contador.repasse.pagar_valor.troco', {
          aplicado: guarani(res.valorAplicado),
          troco: guarani(res.valorNaoUtilizado),
        }),
        life: 8000,
      });
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('contador.repasse.pagar_valor.sucesso'),
    });
  }

  // ===========================================================================
  // Acao — marcar como pago
  // ===========================================================================

  abrirDialogPagar(comissao: ComissaoAdminConsultaDTO): void {
    this.comissaoSelecionada = comissao;
    // Pre-preenche com o saldo -> pagamento integral por padrao. Reduzir = parcial.
    this.valorPagar = comissao.saldo;
    this.erroValorPagar = '';
    this.pagarDialogVisivel = true;
  }

  fecharDialogPagar(): void {
    this.pagarDialogVisivel = false;
    this.comissaoSelecionada = null;
    this.valorPagar = 0;
    this.erroValorPagar = '';
  }

  /** Limpa o erro do campo ao editar (feedback imediato). */
  onValorPagarChange(): void {
    this.erroValorPagar = '';
  }

  confirmarPagar(): void {
    if (this.processando || !this.comissaoSelecionada) {
      return;
    }

    // Validacao client-side: 0 < valor <= saldo. O backend revalida (422 amigavel).
    const saldo = this.comissaoSelecionada.saldo;
    const valor = this.valorPagar;
    if (valor == null || valor <= 0 || valor > saldo) {
      this.erroValorPagar = this.translate.instant('contador.repasse.pagar.valor_invalido');
      return;
    }

    this.processando = true;
    const id = this.comissaoSelecionada.id;

    // Enviamos sempre o valor (explicito). valor === saldo quita integralmente;
    // valor < saldo registra pagamento parcial (comissao segue PENDENTE).
    this.indicacaoService.marcarRepassePago(id, valor).subscribe({
      next: (atualizada) => {
        // Captura o id do pagamento NO INICIO (antes dos resets de estado).
        const pagamentoId = atualizada?.pagamentoComissaoId ?? null;
        this.processando = false;
        this.pagarDialogVisivel = false;
        this.comissaoSelecionada = null;
        this.valorPagar = 0;
        this.erroValorPagar = '';
        // Saldo remanescente > 0 = pagamento parcial (comissao segue pendente);
        // saldo 0 = comissao quitada. Toast diferenciado.
        const parcial = (atualizada?.saldo ?? 0) > 0;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(
            parcial ? 'contador.repasse.sucesso_pagar_parcial' : 'contador.repasse.sucesso_pagar',
          ),
        });
        // Auto-download do comprovante (best-effort): o repasse JA teve sucesso.
        if (pagamentoId != null) {
          this.baixarComprovante(pagamentoId);
        }
        // Recarrega pagina atual + resumo (statusRepasse, saldo, repassadoEm, totais).
        this.carregar(this.paginaAtual(), this.rows);
      },
      error: (err) => {
        this.processando = false;
        this.tratarErroAcao(err);
      },
    });
  }

  // ===========================================================================
  // Acao — marcar como pagas em lote
  // ===========================================================================

  /**
   * Habilita a selecao multipla (checkboxes) apenas quando todas as comissoes
   * visiveis pertencem a UM UNICO indicador. As acoes em lote (pagar/cancelar)
   * so fazem sentido para um indicador. Na pratica exige o filtro de indicador
   * ativo; a verificacao do Set e defensiva: mesmo com o filtro aplicado, so
   * libera se as linhas carregadas forem realmente de um unico `indicadorEmpresaId`.
   */
  get podeSelecionar(): boolean {
    if (this.indicadorSelecionadoId == null) {
      return false;
    }
    const distintos = new Set(this.comissoes.map((c) => c.indicadorEmpresaId));
    return distintos.size <= 1;
  }

  /**
   * Exibe a dica "selecione um indicador" quando a selecao esta bloqueada mas ha
   * comissoes na tela para selecionar (evita mostrar a dica com a lista vazia).
   */
  get mostrarHintSelecao(): boolean {
    return !this.loading && !this.erroCarregar && this.comissoes.length > 0 && !this.podeSelecionar;
  }

  /**
   * Predicado do `[rowSelectable]` da p-table: so PENDENTES sao selecionaveis e
   * somente quando a selecao em massa esta habilitada (indicador unico).
   * Cobre "selecionar todos" (header checkbox), clique na linha e ctrl/shift+clique.
   * Arrow function para preservar o `this`.
   */
  isRowSelectable = (event: { data: ComissaoAdminConsultaDTO; index: number }): boolean => {
    return this.podeSelecionar && this.podePagar(event.data);
  };

  abrirDialogLote(): void {
    if (this.selecionadas.length === 0) {
      return;
    }
    // Monta a lista editavel: uma linha por PENDENTE selecionada (defensivo), com o
    // valor pre-preenchido com o saldo (quitacao integral por padrao; reduzir = parcial).
    this.loteItens = this.selecionadas
      .filter((c) => this.podePagar(c))
      .map((c) => ({ comissao: c, valor: c.saldo, erro: '' }));
    this.loteDialogVisivel = true;
  }

  fecharDialogLote(): void {
    this.loteDialogVisivel = false;
    this.loteItens = [];
  }

  /** Valida um item ao editar (0 < valor <= saldo) e atualiza o erro inline. */
  onLoteItemChange(item: LoteItemEdicao): void {
    item.erro = this.itemLoteInvalido(item)
      ? this.translate.instant('contador.repasse.lote.valor_invalido')
      : '';
  }

  /** Regra de validacao de um item do lote: 0 < valor <= saldo. */
  private itemLoteInvalido(item: LoteItemEdicao): boolean {
    return item.valor == null || item.valor <= 0 || item.valor > item.comissao.saldo;
  }

  /** Soma dos valores editados (total a pagar exibido no rodape do dialog). */
  get loteTotal(): number {
    return this.loteItens.reduce((soma, it) => soma + (it.valor ?? 0), 0);
  }

  /** Bloqueia o "Confirmar" enquanto houver qualquer item invalido. */
  get loteInvalido(): boolean {
    return this.loteItens.length === 0 || this.loteItens.some((it) => this.itemLoteInvalido(it));
  }

  confirmarLote(): void {
    if (this.processando || this.loteInvalido) {
      return;
    }
    this.processando = true;

    // Envia sempre o valor por item (explicito): valor === saldo quita integral,
    // valor < saldo registra pagamento parcial. Itens invalidos ja sao barrados
    // pelo `loteInvalido` (o botao fica desabilitado).
    const itens = this.loteItens.map((it) => ({ id: it.comissao.id, valor: it.valor }));
    // Guarda o par id->nome ANTES de recarregar (o next limpa a selecao/itens).
    const nomesPorId = new Map(this.loteItens.map((it) => [it.comissao.id, it.comissao.indicadorNome]));

    this.indicacaoService.marcarRepasseLote(itens).subscribe({
      next: (res) => {
        this.processando = false;
        this.loteDialogVisivel = false;
        this.loteItens = [];
        this.notificarResultadoLote(res, nomesPorId);
        // Auto-download dos N comprovantes gerados (best-effort, serializados).
        // `?? []` cobre o default emptyList do backend.
        this.baixarComprovantesLote(res.pagamentosGerados ?? []);
        // Recarrega pagina atual + resumo (limpa a selecao dentro de carregar()).
        this.carregar(this.paginaAtual(), this.rows);
      },
      error: (err) => {
        this.processando = false;
        this.tratarErroAcao(err);
      },
    });
  }

  /**
   * Toast de resumo do lote: sucesso (quitadas + parciais + total pago) quando tudo
   * foi processado; alerta (warn) com o detalhe dos motivos quando houve itens
   * ignorados (lote parcial).
   */
  private notificarResultadoLote(
    res: RepasseLoteResultadoDTO,
    nomesPorId: Map<number, string>,
  ): void {
    if (res.totalIgnoradas > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.repasse.lote.parcial_titulo', {
          pagas: res.totalPagas,
          ignoradas: res.totalIgnoradas,
        }),
        detail: this.montarDetalheIgnoradas(res.ignoradas, nomesPorId),
        life: 8000,
      });
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('contador.repasse.lote.sucesso', {
        quitadas: res.totalQuitadas ?? 0,
        parciais: res.totalParciais ?? 0,
        total: this.formatarGuarani(res.valorTotalPago ?? 0),
      }),
    });
  }

  /** Formata Guarani igual ao pipe guaraniFormat (para uso em mensagens/toasts). */
  private formatarGuarani(valor: number): string {
    return `Gs. ${(valor ?? 0).toLocaleString('es-PY', {
      maximumFractionDigits: 0,
      useGrouping: true,
    })}`;
  }

  /** Monta o detalhe amigavel (uma linha por item ignorado: nome + motivo). */
  private montarDetalheIgnoradas(
    ignoradas: RepasseLoteIgnoradaDTO[],
    nomesPorId: Map<number, string>,
  ): string {
    return (ignoradas || [])
      .map((ig) => {
        const nome = nomesPorId.get(ig.id) || `#${ig.id}`;
        return `${nome}: ${ig.motivo}`;
      })
      .join('\n');
  }

  // ===========================================================================
  // Acao — cancelar comissoes em lote (destrutiva, exige confirmacao)
  // ===========================================================================

  /**
   * Abre a confirmacao destrutiva (dialog wave) antes de cancelar em lote as
   * comissoes selecionadas. So dispara a chamada ao confirmar no dialog.
   */
  cancelarLote(): void {
    if (this.processando || this.selecionadas.length === 0) {
      return;
    }
    this.cancelarLoteDialogVisivel = true;
  }

  /** Fecha o dialog de confirmacao de cancelamento em lote sem executar. */
  fecharDialogCancelarLote(): void {
    this.cancelarLoteDialogVisivel = false;
  }

  /** Confirma o cancelamento em lote a partir do dialog wave. */
  confirmarCancelarLote(): void {
    this.cancelarLoteDialogVisivel = false;
    this.executarCancelarLote();
  }

  private executarCancelarLote(): void {
    if (this.processando || this.selecionadas.length === 0) {
      return;
    }
    this.processando = true;

    // So enviamos PENDENTES (defensivo — a selecao ja restringe a elas).
    const pendentes = this.selecionadas.filter((c) => this.podePagar(c));
    const ids = pendentes.map((c) => c.id);
    // Guarda o par id->nome ANTES de recarregar (o next limpa a selecao).
    const nomesPorId = new Map(pendentes.map((c) => [c.id, c.indicadorNome]));

    this.indicacaoService.cancelarEmLote(ids).subscribe({
      next: (res) => {
        this.processando = false;
        this.notificarResultadoCancelamentoLote(res, nomesPorId);
        // Recarrega pagina atual + resumo (limpa a selecao dentro de carregar()).
        this.carregar(this.paginaAtual(), this.rows);
      },
      error: (err) => {
        this.processando = false;
        this.tratarErroAcao(err);
      },
    });
  }

  /**
   * Toast de resumo do cancelamento em lote: sucesso quando todas foram
   * canceladas; alerta (warn) com o detalhe dos motivos quando houve itens
   * ignorados (nao estavam pendentes).
   */
  private notificarResultadoCancelamentoLote(
    res: CancelamentoLoteResultadoDTO,
    nomesPorId: Map<number, string>,
  ): void {
    if (res.totalIgnoradas > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador.repasse.cancelar_lote.parcial_titulo', {
          canceladas: res.totalCanceladas,
          ignoradas: res.totalIgnoradas,
        }),
        detail: this.montarDetalheIgnoradas(res.ignoradas, nomesPorId),
        life: 8000,
      });
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('contador.repasse.cancelar_lote.sucesso', {
        count: res.totalCanceladas,
      }),
    });
  }

  // ===========================================================================
  // Acao — cancelar comissao
  // ===========================================================================

  abrirDialogCancelar(comissao: ComissaoAdminConsultaDTO): void {
    this.comissaoSelecionada = comissao;
    this.cancelarDialogVisivel = true;
  }

  fecharDialogCancelar(): void {
    this.cancelarDialogVisivel = false;
    this.comissaoSelecionada = null;
  }

  confirmarCancelar(): void {
    if (this.processando || !this.comissaoSelecionada) {
      return;
    }
    this.processando = true;
    const id = this.comissaoSelecionada.id;

    this.indicacaoService.cancelarComissao(id).subscribe({
      next: () => {
        this.processando = false;
        this.cancelarDialogVisivel = false;
        this.comissaoSelecionada = null;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('contador.repasse.sucesso_cancelar'),
        });
        // Recarrega pagina atual + resumo (statusRepasse -> CANCELADA, totais).
        this.carregar(this.paginaAtual(), this.rows);
      },
      error: (err) => {
        this.processando = false;
        this.tratarErroAcao(err);
      },
    });
  }

  private tratarErroAcao(err: any): void {
    const status = err?.status;
    if (status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('contador.repasse.erros.sem_permissao'),
      });
      return;
    }
    if (status === 404) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('contador.repasse.erros.nao_encontrada'),
      });
      return;
    }
    if (status === 422) {
      // 422 = regra de negocio. O backend distingue os casos com mensagem AMIGAVEL
      // ja traduzida (ex.: "valor maior que o saldo pendente do indicador",
      // "comissao nao pode ser paga neste estado"). Exibimos a mensagem REAL; a
      // chave generica fica so como fallback quando o corpo nao traz `message`
      // (senao qualquer 422 exibiria o texto de "estado invalido", que engana).
      this.messageService.add({
        severity: 'error',
        summary:
          this.extrairMensagemBackend(err) ||
          this.translate.instant('contador.repasse.erros.estado_invalido'),
      });
      return;
    }
    this.messageService.add({
      severity: 'error',
      summary: err?.error?.message || this.translate.instant('contador.repasse.erros.generico'),
    });
  }

  // ===========================================================================
  // Colunas "Historico de saldo" e "Pagamentos" (exibicao de valores na tabela)
  // ===========================================================================

  /**
   * Indica se ha pagamento a exibir (total pago > 0), derivado de campos ja presentes
   * na listagem — SEM chamada extra. A coluna "Pagamentos" exibe o valor de `totalPago()`
   * quando true; caso contrario "—". Existem eventos ⟺ saldo abatido, EXCETO CANCELADA
   * (que vem saldo=0 mas nao deve exibir — regra O5 do backend), excluida explicitamente.
   * O guard `saldo != null` espelha o defensivo de `isParcial` (INFO-3 do REVIEW_FE).
   *  PAGA             → saldo 0 < valorComissao          => true
   *  PENDENTE parcial → saldo < valorComissao            => true
   *  PENDENTE integral→ saldo === valorComissao          => false
   *  CANCELADA        → excluida pelo 1o termo           => false
   */
  temPagamentos(comissao: ComissaoAdminConsultaDTO): boolean {
    return (
      comissao.statusRepasse !== 'CANCELADA' &&
      comissao.saldo != null &&
      comissao.saldo < comissao.valorComissao
    );
  }

  /**
   * Total pago da comissao (derivacao canonica do backend, APENAS apresentacao):
   * `valorComissao − saldo`. A coluna "Pagamentos" so exibe este valor quando
   * `temPagamentos()` e true (total pago > 0 e nao cancelada — regra O5); nos demais
   * casos a celula mostra "—". Guard `?? 0` defensivo (espelha `temPagamentos`): quando
   * o valor e exibido, `saldo` ja e nao-nulo. CANCELADA nunca exibe (temPagamentos false),
   * mesmo que a subtracao crua fosse nao-zero (backend devolve saldo=0 para cancelada).
   */
  totalPago(comissao: ComissaoAdminConsultaDTO): number {
    return comissao.valorComissao - (comissao.saldo ?? 0);
  }

  /** "2026-05-25T00:00:00" -> "25/05/2026". Parsing por string (evita fuso). Sem match => "—". */
  dataLabel(iso: string | null | undefined): string {
    if (!iso) {
      return '—';
    }
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
  }

  // ===========================================================================
  // Comprovante de pagamento — auto-download (best-effort, secundario)
  // ===========================================================================

  /**
   * Baixa o comprovante de UM pagamento (best-effort). O repasse JA teve sucesso; se o comprovante
   * falhar, avisamos com `warn` (nao `error`) e NAO quebramos o fluxo. Base64 vazio (defensivo) => warn.
   */
  private baixarComprovante(pagamentoComissaoId: number): void {
    this.indicacaoService
      .gerarComprovantePagamentoPdf(pagamentoComissaoId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (base64) => {
          if (base64 && base64.length > 0) {
            FileUtils.abrirPdf(base64, `Comprovante_Pago_${pagamentoComissaoId}.pdf`);
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('contador.repasse.comprovante.indisponivel'),
            });
          }
        },
        error: (err) => this.tratarErroComprovante(err),
      });
  }

  /**
   * Baixa N comprovantes do lote (um por empresa indicadora), SERIALIZADOS via concatMap para nao
   * disparar N requisicoes simultaneas ao backend nem N cliques instantaneos. Best-effort: cada erro
   * e tratado por `tratarErroComprovante` sem derrubar os demais downloads (catchError => EMPTY).
   */
  private baixarComprovantesLote(gerados: PagamentoGeradoLoteDTO[]): void {
    if (!gerados.length) {
      return;
    }
    from(gerados)
      .pipe(
        concatMap((g) =>
          this.indicacaoService.gerarComprovantePagamentoPdf(g.pagamentoComissaoId).pipe(
            tap((base64) => {
              if (base64 && base64.length > 0) {
                FileUtils.abrirPdf(base64, `Comprovante_Pago_${g.pagamentoComissaoId}.pdf`);
              }
            }),
            catchError((err) => {
              this.tratarErroComprovante(err);
              return EMPTY; // nao derruba os proximos downloads
            }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  /**
   * Imprime o comprovante CONSOLIDADO de uma comissao com pagamento(s) — parcial ou paga
   * (botao da coluna de acoes). Chama GET /comissoes/{id}/comprovantes-pdf, que devolve UM
   * unico PDF (Base64: cabecalho + uma linha por evento de pagamento + rodape de totais),
   * substituindo o antigo multi-download (N recibos por evento, dos quais o navegador so
   * entregava o 1o). Reusa `FileUtils.abrirPdf`. `imprimindoComissaoId` liga o spinner da
   * linha e barra clique-duplo; `finalize` sempre o libera. 422 (comissao sem pagamentos
   * itemizados ou CANCELADA) => warn "indisponivel_comissao" (paridade com o antigo caso
   * "sem pagamento"); demais erros (403/404/generico) => `tratarErroComprovante` (por status).
   */
  imprimirComprovantes(comissao: ComissaoAdminConsultaDTO): void {
    if (this.imprimindoComissaoId != null) {
      return; // ja ha uma impressao em andamento (guarda anti-clique-duplo)
    }
    this.imprimindoComissaoId = comissao.id;
    this.indicacaoService
      .gerarComprovanteConsolidadoComissao(comissao.id)
      .pipe(
        finalize(() => (this.imprimindoComissaoId = null)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (base64) => {
          if (base64 && base64.length > 0) {
            FileUtils.abrirPdf(base64, `Comprovante_Comissao_${comissao.id}.pdf`);
          } else {
            // Defensivo: 200 sem corpo. Nao ha comprovante a baixar -> aviso amigavel.
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('contador.repasse.comprovante.indisponivel_comissao'),
            });
          }
        },
        error: (err) => {
          // 422 = comissao sem pagamentos itemizados (legada PAGA sem itens) OU CANCELADA:
          // paridade com o antigo caso "sem pagamento" -> aviso "indisponivel_comissao".
          if (err?.status === 422) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('contador.repasse.comprovante.indisponivel_comissao'),
            });
            return;
          }
          // Demais (403/404/generico): warn por status (reuso das chaves erros.*).
          this.tratarErroComprovante(err);
        },
      });
  }

  /**
   * Erro do comprovante: SEMPRE `warn` (o pagamento ja teve sucesso — nao assustar/reverter).
   * responseType `text` => `err.error` chega como STRING crua; por isso usamos chaves i18n fixas por
   * status (NAO parseamos a mensagem do backend). 404 inclui pagamentos historicos (pre-V1.86) — esperado.
   */
  private tratarErroComprovante(err: any): void {
    const status = err?.status;
    let chave = 'contador.repasse.comprovante.erros.generico';
    if (status === 403) {
      chave = 'contador.repasse.comprovante.erros.sem_permissao';
    } else if (status === 404) {
      chave = 'contador.repasse.comprovante.erros.nao_encontrado';
    } else if (status === 422) {
      chave = 'contador.repasse.comprovante.erros.inconsistente';
    }
    this.messageService.add({ severity: 'warn', summary: this.translate.instant(chave) });
  }

  // ===========================================================================
  // Helpers de exibicao
  // ===========================================================================

  /** Indica se a comissao admite repasse (apenas PENDENTE). */
  podePagar(comissao: ComissaoAdminConsultaDTO): boolean {
    // Toda comissao PENDENTE e pagavel — inclusive a de cupom PROMOCIONAL (sem empresa
    // indicadora): o pagamento e registrado sem empresa de destino, sem gerar receita.
    return comissao.statusRepasse === 'PENDENTE';
  }

  /** Indica se a comissao admite cancelamento (apenas PENDENTE). */
  podeCancelar(comissao: ComissaoAdminConsultaDTO): boolean {
    return comissao.statusRepasse === 'PENDENTE';
  }

  /**
   * Indica se a linha admite imprimir comprovante(s): comissao com >=1 pagamento
   * (parcial ou paga). Exclui CANCELADA e PENDENTE sem pagamento (saldo integral).
   * Delega em `temPagamentos()` — mesma condicao (saldo ja abatido e nao cancelada).
   */
  podeImprimirComprovante(comissao: ComissaoAdminConsultaDTO): boolean {
    return this.temPagamentos(comissao);
  }

  /**
   * Indica pagamento PARCIAL: comissao ainda PENDENTE, mas com saldo ja abatido
   * (`saldo` menor que o valor total). Alimenta a tag discreta "Parcial" na tabela.
   */
  isParcial(comissao: ComissaoAdminConsultaDTO): boolean {
    return (
      comissao.statusRepasse === 'PENDENTE' &&
      comissao.saldo != null &&
      comissao.saldo < comissao.valorComissao
    );
  }

  /** Nome do indicado com fallback: nome -> email -> traco. */
  nomeIndicado(comissao: ComissaoAdminConsultaDTO): string {
    return comissao.indicadoNome || comissao.emailIndicado || '—';
  }

  labelStatus(status: StatusRepasse | string | undefined): string {
    if (!status) {
      return '';
    }
    return this.translate.instant(`contador.repasse.status.${status}`);
  }

  severityStatus(status: StatusRepasse | string | undefined): TagSeverity {
    switch (status) {
      case 'PAGA':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'CANCELADA':
        return 'danger';
      default:
        return 'info';
    }
  }

  /**
   * Exibe a competencia como "dd/MM/aaaa". O dia agora e o dia de cobranca da
   * inscricao (nao mais o "01" convencional). Aceita o ISO "AAAA-MM-DDTHH:mm:ss";
   * fallback "AAAA-MM" (sem dia) exibe "MM/aaaa". Parsing por string p/ evitar fuso.
   */
  competenciaLabel(competencia: string | null | undefined): string {
    if (!competencia) {
      return '—';
    }
    const completa = competencia.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (completa) {
      return `${completa[3]}/${completa[2]}/${completa[1]}`;
    }
    const mesAno = competencia.match(/^(\d{4})-(\d{2})/);
    return mesAno ? `${mesAno[2]}/${mesAno[1]}` : competencia;
  }

  private paginaAtual(): number {
    return this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
  }
}
